// src/pages/MissionsPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package,
  Clock,
  CheckCircle,
  Filter,
  RefreshCw,
  Search,
  MapPin,
  ChevronLeft
} from 'lucide-react';
import { toast } from 'react-toastify';

// Components
import BottomNav from '../components/common/BottomNav';
import AppLogo from '../components/common/AppLogo';
import MissionCard from '../components/missions/MissionCard';

// Store
import {
  fetchCurrentDeliveries,
  fetchDeliveryHistory,
  selectCurrentDeliveries,
  selectDeliveryHistory,
  selectDriverLoading
} from '../store/slices/driverSlice';

const MissionsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State
  const [activeTab, setActiveTab] = useState('current');
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterZone, setFilterZone] = useState('all');

  // Selectors
  const currentDeliveries = useSelector(selectCurrentDeliveries);
  const deliveryHistory = useSelector(selectDeliveryHistory);
  const loading = useSelector(selectDriverLoading);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      await Promise.all([
        dispatch(fetchCurrentDeliveries()).unwrap(),
        dispatch(fetchDeliveryHistory()).unwrap()
      ]);
    } catch (error) {
      console.error('Error loading missions:', error);
      toast.error('Erreur lors du chargement');
    }
  };

  const refreshData = useCallback(async () => {
    setRefreshing(true);
    try {
      await dispatch(fetchCurrentDeliveries()).unwrap();
    } catch (error) {
      console.error('Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  }, [dispatch]);

  const handleMissionClick = (mission) => {
    navigate(`/driver/mission/${mission.id}`);
  };

  // Filter missions based on search and zone
  const filterMissions = (missions) => {
    if (!missions) return [];

    return missions.filter(mission => {
      const matchesSearch = searchQuery === '' ||
        mission.tracking_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        mission.to_address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        mission.recipient_name?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesZone = filterZone === 'all' || mission.delivery_zone === filterZone;

      return matchesSearch && matchesZone;
    });
  };

  // Get current tab data
  const getTabData = () => {
    switch (activeTab) {
      case 'current':
        return filterMissions(currentDeliveries);
      case 'history':
        return filterMissions(deliveryHistory);
      default:
        return [];
    }
  };

  const tabs = [
    {
      id: 'current',
      label: 'Mes livraisons',
      icon: Clock,
      count: currentDeliveries?.length || 0,
      color: 'blue'
    },
    {
      id: 'history',
      label: 'Historique',
      icon: CheckCircle,
      count: deliveryHistory?.length || 0,
      color: 'emerald'
    }
  ];

  const missions = getTabData();
  const isLoading = loading.currentDeliveries;

  // Get unique zones for filter
  const zones = [...new Set(
    (currentDeliveries || []).map(d => d.delivery_zone).filter(Boolean)
  )];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200/50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/driver')}
                className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </motion.button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Livraisons</h1>
                <p className="text-sm text-gray-500">
                  {missions.length} livraison{missions.length > 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={refreshData}
                disabled={refreshing}
                className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center"
              >
                <RefreshCw className={`w-5 h-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
              </motion.button>
              <AppLogo size={32} />
            </div>
          </div>

          {/* Search Bar */}
          <div className="flex gap-2 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
              />
            </div>

            {zones.length > 0 && (
              <div className="relative">
                <select
                  value={filterZone}
                  onChange={(e) => setFilterZone(e.target.value)}
                  className="appearance-none pl-10 pr-8 py-2.5 bg-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
                >
                  <option value="all">Toutes les zones</option>
                  {zones.map(zone => (
                    <option key={zone} value={zone}>{zone}</option>
                  ))}
                </select>
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <motion.button
                  key={tab.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-2.5 px-3 rounded-xl flex items-center justify-center gap-2 transition-all ${
                    isActive
                      ? tab.color === 'orange'
                        ? 'bg-orange-100 text-orange-700'
                        : tab.color === 'blue'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-emerald-100 text-emerald-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{tab.label}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    isActive ? 'bg-white/50' : 'bg-gray-200'
                  }`}>
                    {tab.count}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-4 pb-24 md:pb-4">
        {/* Loading State */}
        {isLoading && missions.length === 0 && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-4 animate-pulse">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-gray-200 rounded-xl" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-16" />
                  </div>
                  <div className="text-right">
                    <div className="h-5 bg-gray-200 rounded w-16 mb-1" />
                    <div className="h-3 bg-gray-200 rounded w-10" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-full" />
                  <div className="h-3 bg-gray-200 rounded w-3/4" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && missions.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {activeTab === 'current' ? 'Aucune livraison en cours' : 'Aucun historique'}
            </h3>
            <p className="text-gray-500">
              {activeTab === 'current'
                ? 'Les livraisons qui vous sont assignées apparaîtront ici'
                : 'Vos livraisons terminées apparaîtront ici'}
            </p>
          </motion.div>
        )}

        {/* Mission List */}
        <AnimatePresence mode="popLayout">
          <div className="space-y-3">
            {missions.map((mission, index) => (
              <MissionCard
                key={mission.id}
                mission={mission}
                variant={activeTab === 'current' ? 'current' : 'completed'}
                onView={handleMissionClick}
                onNavigate={(m) => {
                  const url = `https://www.google.com/maps/dir/?api=1&destination=${m.delivery_latitude},${m.delivery_longitude}`;
                  window.open(url, '_blank');
                }}
                onComplete={(m) => navigate(`/driver/mission/${m.id}`)}
                onCall={(phone) => window.open(`tel:${phone}`)}
                index={index}
              />
            ))}
          </div>
        </AnimatePresence>
      </main>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default MissionsPage;
